import { gql, NetworkStatus, useMutation, useQuery } from '@apollo/client'
import { useEffect } from 'react'

import { useGetNewestData } from '@/clients/utoniq-core'
import {
  ArtistPostsFragmentDoc,
  ArtistPostVideosFragmentDoc,
  ArtistPostVideosToCurrentVideoQuery,
  ArtistPostVideosToCurrentVideoQueryVariables,
  BlockUser,
  BlockUserMutation,
  BlockUserMutationVariables,
  CurrentUserFragmentDoc,
  FetchNewArtistPostVideosToCurrentVideoQuery,
  FetchNewArtistPostVideosToCurrentVideoQueryVariables,
  LikePost,
  LikePostMutation,
  LikePostMutationVariables,
  PostBoxFragmentDoc,
  PostDetailQuery,
  PostDetailQueryVariables,
  PostType,
  StoryFilter,
  UnlikePost,
  UnlikePostMutation,
  UnlikePostMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useCurrentUser, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { openReportForm } from '@/core/googleForm'

import { Action, State } from './template'
/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, routerPages, generatePath } = useRouter<{ postId: string }>()
  const { user } = useCurrentUser()

  const { data: postData, loading: loadingPost } = useQuery<
    Pick<PostDetailQuery, 'post'>,
    PostDetailQueryVariables
  >(POST_DETAIL, {
    variables: { postId: router.query.postId },
    returnPartialData: true,
  })
  const artistId = postData?.post?.artistId ?? config.artistId ?? ''
  const postId = router.query.postId
  const categoryId = postData?.post?.categoryId ?? null

  // NOTE posts/{postId}/videoへのアクセスであっても投稿タイプが動画でない場合にリダイレクトさせる
  useEffect(() => {
    const autoRedirect = () => {
      const isVideoPost =
        postData?.post?.resourceType === PostType.Video &&
        !!postData?.post?.resource?.uri
      if (!postData?.post?.id) return
      if (!isVideoPost) {
        router.replace(
          generatePath(routerPages.PostDetail, { postId: postData?.post?.id })
        )
      }
    }

    autoRedirect()
  }, [postData?.post])
  const resourceType = PostType.Video

  const { isFirstLoadRef } = useGetNewestData<
    FetchNewArtistPostVideosToCurrentVideoQuery,
    FetchNewArtistPostVideosToCurrentVideoQueryVariables
  >({
    query: FETCH_NEW_ARTIST_POST_VIDEOS_TO_CURRENT_VIDEO,
    variables: {
      artistId,
      resourceType,
      storyFilter: StoryFilter.Include,
      pageInput: { limit: 12 }, // NOTE: utoniqPaginationのmerge処理に必要なため削除NG
      pageInputById: { limit: 12 },
      categoryId: categoryId,
    },
    identify: { __typename: 'Artist', id: artistId },
    fields: [
      {
        fieldName: 'posts',
        responsePath: 'artist.posts',
        fragment: PostBoxFragmentDoc,
      },
    ],
  })
  const { data, fetchMore, networkStatus, loading, refetch } = useQuery<
    ArtistPostVideosToCurrentVideoQuery,
    ArtistPostVideosToCurrentVideoQueryVariables
  >(ARTIST_POST_VIDEOS, {
    variables: {
      artistId,
      resourceType,
      storyFilter: StoryFilter.Include,
      pageInput: { limit: 12 }, // NOTE: utoniqPaginationのmerge処理に必要なため削除NG
      pageInputById: { limit: 12 },
      categoryId: categoryId,
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      isFirstLoadRef.current = false
    },
    skip: !postData?.post,
  })
  const isNextFetch = !!data?.artist?.posts
  const pageInfo = data?.artist?.posts?.pageInfo
  const fetchMorePosts = async () => {
    if (pageInfo && pageInfo.hasNextPage) {
      await fetchMore({
        variables: {
          artistId,
          // NOTE: utoniqPaginationのmerge処理に必要なため削除NG
          pageInput: {
            cursor: pageInfo.endCursor || null,
            limit: 10,
          },
          pageInputById: {
            cursor: pageInfo.endCursor || null,
            limit: 10,
          },
        },
      })
    }
  }
  const [likePost] = useMutation<LikePostMutation, LikePostMutationVariables>(
    LikePost
  )

  const [unlikePost] = useMutation<
    UnlikePostMutation,
    UnlikePostMutationVariables
  >(UnlikePost)

  // アーティストブロック機能
  const [blockUser] = useMutation<
    BlockUserMutation,
    BlockUserMutationVariables
  >(BlockUser)

  // NOTE リクエストされたpostIdが現れるまでfetch moreし続ける
  useEffect(() => {
    if (!data?.artist?.posts.edges.some(edge => edge.node.id === postId)) {
      fetchMorePosts()
    }
  }, [data?.artist?.posts.edges.length])

  const nodes =
    data?.artist?.posts.edges
      // NOTE YouTube動画は対象外とする
      .filter(({ node }) => node.resource?.uri !== null)
      .map(({ node }) => node) || []

  const postIndex = nodes.findIndex(node => node.id === postId) ?? undefined

  const onClose = () => {
    router.goBack(generatePath(routerPages.PostDetail, { postId }))
  }

  return {
    state: {
      user: data?.user,
      isInvalidPost: !loadingPost && postData?.post === null,
      posts: nodes,
      // NOTE 初期表示時、postIdに一致するポストまで取得し続ける処理の完了しているかどうかを表現する
      initialLoading:
        (loadingPost ?? loading ?? !isNextFetch) && postIndex === undefined,
      refetching: networkStatus === NetworkStatus.fetchMore,
      artistName: data?.artist?.displayName || '',
      hasNextPage: !!pageInfo?.hasNextPage,
      postIndex,
    },
    actions: {
      onFetchMore: fetchMorePosts,
      async onLikePost(postId, isLike, likeCount) {
        if (isLike) {
          await unlikePost({
            variables: { unlikePostPostId: postId },
            optimisticResponse: ({ unlikePostPostId }) => ({
              unlikePost: {
                __typename: 'Post',
                id: unlikePostPostId,
                isLike: false,
                likeCount: --likeCount,
              },
            }),
          })
          return
        }
        await likePost({
          variables: { likePostPostId: postId },
          optimisticResponse: ({ likePostPostId }) => ({
            likePost: {
              __typename: 'Post',
              id: likePostPostId,
              isLike: true,
              likeCount: ++likeCount,
            },
          }),
        })
      },
      async onReportArtist(contentId, userId, accountId) {
        await openReportForm({
          email: user?.email ?? '',
          contentType: 'post',
          contentId,
          userId,
          accountId,
        })
      },
      async onBlockUser(id) {
        await blockUser({ variables: { id: id } })
        await refetch()
      },
      onClose,
    },
  }
})

const POST_DETAIL = gql`
  query VideoDetail($postId: ID!) {
    post(id: $postId) {
      ...PostBox
    }
  }
  ${PostBoxFragmentDoc}
`
const ARTIST_POST_VIDEOS = gql`
  query ArtistPostVideosToCurrentVideo(
    $artistId: ID!
    $pageInput: PageInputDate
    $pageInputById: PageInput
    $resourceType: PostType
    $storyFilter: StoryFilter
    $categoryId: String
  ) {
    artist(id: $artistId) {
      id
      displayName
      posts(
        pageInput: $pageInput
        pageInputById: $pageInputById
        resourceType: $resourceType
        storyFilter: $storyFilter
        categoryId: $categoryId
      ) {
        ...ArtistPostVideos
      }
      currentLive {
        id
      }
    }
    user {
      ...CurrentUser
    }
  }
  ${ArtistPostVideosFragmentDoc}
  ${CurrentUserFragmentDoc}
`

const FETCH_NEW_ARTIST_POST_VIDEOS_TO_CURRENT_VIDEO = gql`
  query FetchNewArtistPostVideosToCurrentVideo(
    $artistId: ID!
    $pageInput: PageInputDate
    $pageInputById: PageInput
    $resourceType: PostType
    $storyFilter: StoryFilter
    $categoryId: String
  ) {
    artist(id: $artistId) {
      id
      posts(
        pageInput: $pageInput
        pageInputById: $pageInputById
        resourceType: $resourceType
        storyFilter: $storyFilter
        categoryId: $categoryId
      ) {
        ...ArtistPosts
      }
      currentLive {
        id
      }
    }
  }
  ${ArtistPostsFragmentDoc}
`
